import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../../../helpers'
import { ListVendor, PlanStatus } from '../../../models'
import {
    LIST_MODE_FEATURED,
    LIST_MODE_NEARBY,
    LIST_MODE_RECOMMENDED,
    ListMode,
    NVLVendor,
    mapListVendorToNVL,
    normalizeNVLVendor,
} from './nvlVendorModel'

export const NVL_VENDORS_QUERY_KEY = 'nvl-vendors'
export const DEFAULT_NVL_PAGE_SIZE = 10

export type NVLRatingSort = 'default' | 'rating_desc' | 'rating_asc'

export interface NVLVendorFilters {
    services: Array<{ id: number; name: string }>
    trained: 'all' | 'yes' | 'no' | string
    willingToTravel: 'all' | 'yes' | 'no' | string
    /** null / undefined = no distance filter (All) */
    radius: number | null
    /** Server-side name search used by Search all */
    searchText: string
    /** Exact vendor from autocomplete; takes precedence over searchText */
    vendorId?: number | null
    /** Minimum national rating inclusive; null = All */
    minRating?: number | null
    /** Sort by stored national rating */
    sortBy?: NVLRatingSort
}

/** Default filters for Featured (home + list) so they share one React Query cache entry. */
export const EMPTY_NVL_FILTERS: NVLVendorFilters = {
    services: [],
    trained: 'all',
    willingToTravel: 'all',
    radius: null,
    searchText: '',
    vendorId: null,
    minRating: null,
    sortBy: 'default',
}

export interface NVLVendorsQueryResult {
    vendors: NVLVendor[]
    count: number
    page: number
    pageSize: number
    fallbackToNearby: boolean
    fallbackMessage: string | null
    emptyMessage: string | null
}

interface PaginatedNVLResponse {
    count: number
    page: number
    page_size: number
    results: NVLVendor[]
}

const buildNVLParams = (
    mode: ListMode,
    filters: NVLVendorFilters,
    page: number,
    pageSize: number,
) => {
    let will_travel = ''
    if (filters.willingToTravel === 'yes') {
        will_travel = 'True'
    } else if (filters.willingToTravel === 'no') {
        will_travel = 'False'
    }

    let is_trained = ''
    if (filters.trained === 'yes') {
        is_trained = 'True'
    } else if (filters.trained === 'no') {
        is_trained = 'False'
    }

    let services = ''
    if (filters.services.length > 0) {
        filters.services.forEach((el, index) => {
            if (index !== filters.services.length - 1) {
                services = services + `${el.id}, `
            } else {
                services = services + `${el.id}`
            }
        })
    }

    const params: {
        is_trained?: string
        will_travel?: string
        radius?: string
        services?: string
        plan?: string
        q?: string
        vendor_id?: number
        min_rating?: string
        ordering?: string
        page: number
        page_size: number
    } = {
        page,
        page_size: pageSize,
    }
    if (is_trained !== '') {
        params.is_trained = is_trained
    }
    if (will_travel !== '') {
        params.will_travel = will_travel
    }
    // Featured is distance-ordered with no radius cutoff.
    if (
        mode !== LIST_MODE_FEATURED &&
        filters.radius != null &&
        filters.radius > 0
    ) {
        params.radius = String(filters.radius)
    }
    if (services.length !== 0) {
        params.services = JSON.stringify(services)
    }
    if (mode === LIST_MODE_FEATURED) {
        params.plan = PlanStatus.PREMIUM
    }
    if (filters.vendorId != null && filters.vendorId > 0) {
        params.vendor_id = filters.vendorId
    } else {
        const vendorQuery = (filters.searchText || '').trim()
        if (vendorQuery) {
            params.q = vendorQuery
        }
    }
    if (filters.minRating != null && filters.minRating > 0) {
        params.min_rating = String(filters.minRating)
    }
    if (filters.sortBy === 'rating_asc') {
        params.ordering = 'rating'
    } else if (filters.sortBy === 'rating_desc') {
        params.ordering = '-rating'
    }
    return params
}

const fetchNVLVendors = async (
    mode: ListMode,
    filters: NVLVendorFilters,
    page: number,
    pageSize: number,
): Promise<
    Pick<NVLVendorsQueryResult, 'vendors' | 'count' | 'page' | 'pageSize'>
> => {
    const params = buildNVLParams(mode, filters, page, pageSize)
    const res = await axiosInstance.get<PaginatedNVLResponse>(
        `company/national_vendor_list/`,
        { params },
    )
    const data = res.data
    return {
        vendors: (data.results ?? []).map(normalizeNVLVendor),
        count: data.count ?? 0,
        page: data.page ?? page,
        pageSize: data.page_size ?? pageSize,
    }
}

const fetchRecommendedVendors = async (
    filters: NVLVendorFilters,
    page: number,
    pageSize: number,
): Promise<NVLVendorsQueryResult> => {
    try {
        const res = await axiosInstance.get(
            'company/vendor/recommend_vendors_for_property/',
        )
        let mapped = ((res.data?.vendors ?? []) as ListVendor[]).map(
            mapListVendorToNVL,
        )

        if (filters.services.length > 0) {
            const serviceIds = new Set(
                filters.services.map((service) => service.id),
            )
            const sourceVendors = (res.data?.vendors ?? []) as ListVendor[]
            mapped = mapped.filter((vendor) => {
                const listVendor = sourceVendors.find(
                    (item) => item.id === vendor.id,
                )
                return listVendor?.services?.some((service) =>
                    serviceIds.has(service.id),
                )
            })
        }

        if (filters.trained === 'yes') {
            mapped = mapped.filter((vendor) => vendor.is_trained)
        } else if (filters.trained === 'no') {
            mapped = mapped.filter((vendor) => !vendor.is_trained)
        }

        if (filters.willingToTravel === 'yes') {
            mapped = mapped.filter((vendor) => vendor.will_travel)
        } else if (filters.willingToTravel === 'no') {
            mapped = mapped.filter((vendor) => !vendor.will_travel)
        }

        if (filters.radius != null && filters.radius > 0) {
            mapped = mapped.filter(
                (vendor) =>
                    vendor.distance != null &&
                    vendor.distance <= filters.radius!,
            )
        }

        if (filters.vendorId != null && filters.vendorId > 0) {
            mapped = mapped.filter((vendor) => vendor.id === filters.vendorId)
        } else if ((filters.searchText || '').trim()) {
            const query = filters.searchText.trim().toLocaleLowerCase()
            mapped = mapped.filter((vendor) =>
                vendor.name.toLocaleLowerCase().includes(query),
            )
        }

        if (filters.minRating != null && filters.minRating > 0) {
            mapped = mapped.filter(
                (vendor) => (vendor.rating ?? 0) >= filters.minRating!,
            )
        }

        if (filters.sortBy === 'rating_desc') {
            mapped = [...mapped].sort(
                (a, b) => (b.rating ?? 0) - (a.rating ?? 0),
            )
        } else if (filters.sortBy === 'rating_asc') {
            mapped = [...mapped].sort(
                (a, b) => (a.rating ?? 0) - (b.rating ?? 0),
            )
        }

        if (mapped.length === 0) {
            return {
                vendors: [],
                count: 0,
                page,
                pageSize,
                fallbackToNearby: true,
                fallbackMessage:
                    'There are no recommendations in your area. Showing nearby vendors instead.',
                emptyMessage: null,
            }
        }

        const start = (page - 1) * pageSize
        return {
            vendors: mapped.slice(start, start + pageSize),
            count: mapped.length,
            page,
            pageSize,
            fallbackToNearby: false,
            fallbackMessage: null,
            emptyMessage: null,
        }
    } catch (err: any) {
        const status = err?.response?.status
        const message = err?.response?.data?.message
        if (status === 400) {
            return {
                vendors: [],
                count: 0,
                page,
                pageSize,
                fallbackToNearby: true,
                fallbackMessage:
                    message ||
                    'There are no recommendations in your area. Showing nearby vendors instead.',
                emptyMessage: null,
            }
        }
        return {
            vendors: [],
            count: 0,
            page,
            pageSize,
            fallbackToNearby: true,
            fallbackMessage:
                'Unable to load recommended vendors. Showing nearby vendors instead.',
            emptyMessage: null,
        }
    }
}

const fetchVendorsForMode = async (
    mode: ListMode,
    filters: NVLVendorFilters,
    page: number,
    pageSize: number,
): Promise<NVLVendorsQueryResult> => {
    if (mode === LIST_MODE_RECOMMENDED) {
        return fetchRecommendedVendors(filters, page, pageSize)
    }

    const pageResult = await fetchNVLVendors(mode, filters, page, pageSize)
    let emptyMessage: string | null = null

    if (pageResult.count === 0) {
        if (filters.radius != null && filters.radius > 0) {
            emptyMessage =
                'No vendors found within this distance. Try increasing the radius or choose All.'
        } else if (mode === LIST_MODE_NEARBY) {
            emptyMessage =
                'No vendors found within this distance. Try increasing the radius.'
        } else if (mode === LIST_MODE_FEATURED) {
            emptyMessage =
                'No featured vendors found. Featured vendors have a PREMIUM plan.'
        } else if (filters.vendorId != null && filters.vendorId > 0) {
            emptyMessage = 'That vendor was not found.'
        } else if ((filters.searchText || '').trim()) {
            emptyMessage = `No vendors matched “${filters.searchText.trim()}”. Try a different name or clear the search.`
        } else if (filters.minRating != null && filters.minRating > 0) {
            emptyMessage = `No vendors with a ${filters.minRating}+ star rating match the current filters.`
        }
    }

    return {
        ...pageResult,
        fallbackToNearby: false,
        fallbackMessage: null,
        emptyMessage,
    }
}

export const useNVLVendors = (
    mode: ListMode,
    filters: NVLVendorFilters,
    page: number,
    pageSize: number = DEFAULT_NVL_PAGE_SIZE,
) => {
    // Featured ignores radius so home + list share one cache entry for the default view.
    const queryFilters: NVLVendorFilters =
        mode === LIST_MODE_FEATURED ? { ...filters, radius: null } : filters

    const query = useQuery<NVLVendorsQueryResult, Error>({
        queryKey: [NVL_VENDORS_QUERY_KEY, mode, queryFilters, page, pageSize],
        queryFn: () => fetchVendorsForMode(mode, queryFilters, page, pageSize),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        staleTime: 30 * 1000,
    })

    return {
        vendors: query.data?.vendors ?? [],
        count: query.data?.count ?? 0,
        page: query.data?.page ?? page,
        pageSize: query.data?.pageSize ?? pageSize,
        fallbackToNearby: query.data?.fallbackToNearby ?? false,
        fallbackMessage: query.data?.fallbackMessage ?? null,
        emptyMessage: query.data?.emptyMessage ?? null,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error,
    }
}
